import React, {ReactElement} from "react";
import {graphql, PageProps} from "gatsby";
import Sticky from "react-stickynode";

import Layout from "/src/components/layouts/layout/Layout";
import HeaderInfo from "/src/components/header-info/HeaderInfo";
import JobDataProvider from "/src/components/jobs/job-data-provider/JobDataProvider";
import JobFilter from "/src/components/jobs/job-filter/JobFilter";
import JobResults from "/src/components/jobs/job-results/JobResults";
import {ImageObject} from "/src/components/image/Image";

import useGreenhouse from "/src/hooks/useGreenhouse";

import translate from "/src/utils/translate";

import * as styles from "./jobs.module.scss";

export type GreenhouseDept =
  NonNullable<Queries.GreenhouseDepartmentEdge>["node"];

export type GreenhouseJob = NonNullable<Queries.GreenhouseJobEdge>["node"];

export type GreenhouseOffice =
  NonNullable<Queries.GreenhouseOfficeEdge>["node"];

const JobsPage = ({data}: PageProps<any>): ReactElement => {
  const {contentfulPromoBanner, contentfulAsset} = data;

  const {offices, jobs, depts} = useGreenhouse();

  return (
    <Layout promoBannerData={contentfulPromoBanner}>
      <HeaderInfo
        description={translate(
          "Flexport moves freight globally by air, ocean, rail, and truck for the world's leading brands. Only Flexport delivers deep visibility and control, low and predictable supply chain costs, and faster and more reliable transit times."
        )}
        image={contentfulAsset as ImageObject}
        title={translate("Careers at Flexport | Open Positions")}
      />

      <JobDataProvider
        allJobs={jobs}
        allDepts={depts}
        allOffices={offices}
        handleHistory
      >
        {jobsData => {
          return (
            <>
              <JobFilter
                darkMode
                showOpenings
                showClearFilter
                breadcrumbs={[
                  {
                    label: translate("Back to Careers"),
                    href: "/company/careers/",
                  },
                ]}
                headline={translate("Find Your Role at Flexport")}
                {...jobsData}
              />
              <Sticky innerActiveClass={styles.jobsPage__sticky}>
                {status => {
                  if (status.status === Sticky.STATUS_FIXED) {
                    return (
                      <JobFilter
                        darkMode
                        showOpenings
                        compactMode
                        headline={translate("Find Your Role at Flexport")}
                        {...jobsData}
                      />
                    );
                  }
                }}
              </Sticky>
              <JobResults results={jobsData.jobResults} />
            </>
          );
        }}
      </JobDataProvider>
    </Layout>
  );
};

export default JobsPage;

export const query = graphql`
  query JobsPageQuery($locale: String!) {
    contentfulPromoBanner(
      node_locale: {eq: $locale}
      contentful_id: {eq: "17z8sGPISXmsBtu2jynvvp"}
    ) {
      ...PromoBannerFragment
    }
    contentfulAsset(contentful_id: {eq: "5O34zKjkPih5j8nJ4ByHHU"}) {
      ... on ContentfulAsset {
        ...AssetFragment
      }
      gatsbyImageData
    }
  }
`;
